import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { LinkSimple, Copy, ArrowSquareOut, EnvelopeSimple, DownloadSimple, Printer, QrCode } from "@phosphor-icons/react";
import { copyText } from "../lib/tenantPortal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function LoginLinkCard({ companyName }) {
  const [link, setLink] = useState(null);

  useEffect(() => {
    API.get("/tenant/login-link").then(({ data }) => setLink(data)).catch(() => setLink(false));
  }, []);

  if (link === null || link === false) return null;

  const display = link.url.replace(/^https?:\/\//, "");
  const qrSrc = `${BACKEND}${link.qr_path}?size=240`;
  const qrDownload = `${BACKEND}${link.qr_path}?size=1024`;
  const copy = async () => {
    (await copyText(link.url)) ? toast.success("Login link copied") : toast.error("Copy failed — select the link and copy manually");
  };
  const mailto = `mailto:?subject=${encodeURIComponent(`Sign in to ${companyName || "our workspace"}`)}&body=${encodeURIComponent(`Here's our team sign-in link — bookmark it:\n${link.url}`)}`;

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="branding-login-link-card">
      <div className="flex items-center gap-2 mb-1">
        <LinkSimple size={16} className="text-primary" />
        <h2 className="text-sm font-bold font-[Chivo]">Your login link</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Share this address with your team. Anyone opening it sees your logo and colours, and only members of {companyName || "your workspace"} can sign in there.
      </p>

      <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 mb-3">
            <code className="text-sm font-mono truncate flex-1" data-testid="branding-login-link-url">{display}</code>
            <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0" onClick={copy} data-testid="branding-login-link-copy">
              <Copy size={14} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={copy} data-testid="branding-login-link-copy-btn"><Copy size={13} /> Copy link</Button>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" asChild>
              <a href={mailto} data-testid="branding-login-link-email"><EnvelopeSimple size={13} /> Email it</a>
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" asChild>
              <a href={link.url} target="_blank" rel="noreferrer" data-testid="branding-login-link-open"><ArrowSquareOut size={13} /> Preview</a>
            </Button>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-white p-3" data-testid="branding-login-qr">
          <img src={qrSrc} alt={`QR code for ${display}`} width={120} height={120} className="h-[120px] w-[120px]" data-testid="branding-login-qr-img" />
          <p className="text-[10px] text-slate-500 flex items-center gap-1"><QrCode size={12} /> Scan to sign in</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1 px-2" asChild>
              <a href={qrDownload} download={`${link.slug}-login-qr.png`} data-testid="branding-login-qr-download"><DownloadSimple size={12} /> PNG</a>
            </Button>
            <Button size="sm" className="h-7 text-[11px] gap-1 px-2" asChild>
              <a href="/login-poster" target="_blank" rel="noreferrer" data-testid="branding-login-link-poster"><Printer size={12} /> Print poster</a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
